import { gql } from "@apollo/client";
import { MEDIA_ITEM_FRAGMENT } from "fragments";
import { FlexibleContentBlocksPhotoGallery, MediaItem } from "graphql";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";

interface PhotoGalleryProps
  extends FlexibleContentBlocksPhotoGallery {
  className?: string;
}

const PhotoGallery = ({
  className,
  images,
  galleryTitle: title,
}: PhotoGalleryProps) => {
  const imageNodes = (images?.nodes as MediaItem[] | undefined)?.slice(0, 6) || [];

  const getImage = (i: number): MediaItem | undefined => imageNodes[i];

  return (
    <div
      className={clsx(
        "relative container max-w-7xl mx-auto py-6 md:py-12 flex flex-col gap-6 px-4",
        className
      )}
    >
      <div className="relative z-10 container mx-auto max-xl:px-4 md:px-0">

        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-6 md:gap-16 items-center">

          {/* LEFT */}
          <div className="flex flex-col gap-4 order-2 md:order-1">
            <p className="text-left font-accent text-3xl font-bold text-secondary">
              Gallery
            </p>

            {title && (
              <h2 className="text-4xl uppercase font-serif text-primary">
                {title}
              </h2>
            )}

            <p className="text-base font-sans text-primary">
              Explore our latest collection of featured imagery and moments
              captured throughout our work.
            </p>

            <Link
              href="/gallery"
              className="flex items-center uppercase justify-center text-center px-4 py-2 rounded-lg font-bold min-w-[150px] font-sans transition duration-300 ease-in-out bg-primary text-white hover:bg-white hover:text-primary border-2 border-primary w-fit"
            >
              View Gallery
            </Link>
          </div>

          {/* RIGHT */}
          <div className="flex flex-col gap-2 order-1 md:order-2">

            {/* ROW 1 */}
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 h-[270px]">

              {(() => {
                const img = getImage(0);
                return (
                  img?.sourceUrl && (
                    <div className="col-span-2 md:col-span-2 relative w-full h-full overflow-hidden rounded-lg">
                      <Image
                        src={img.sourceUrl}
                        alt={img.altText || ""}
                        fill
                        className="object-cover"
                        sizes="100vw"
                      />
                    </div>
                  )
                );
              })()}

              {(() => {
                const img = getImage(1);
                return (
                  img?.sourceUrl && (
                    <div className="col-span-1 relative w-full h-full overflow-hidden rounded-lg">
                      <Image
                        src={img.sourceUrl}
                        alt={img.altText || ""}
                        fill
                        className="object-cover"
                        sizes="100vw"
                      />
                    </div>
                  )
                );
              })()}

              {(() => {
                const img = getImage(2);
                return (
                  img?.sourceUrl && (
                    <div className="col-span-1 hidden md:block relative w-full h-full overflow-hidden rounded-lg">
                      <Image
                        src={img.sourceUrl}
                        alt={img.altText || ""}
                        fill
                        className="object-cover"
                        sizes="100vw"
                      />
                    </div>
                  )
                );
              })()}

            </div>

            {/* ROW 2 */}
            <div className="grid grid-cols-3 gap-3 h-[160px]">

              {[3, 4, 5].map((i) => {
                const img = getImage(i);

                return (
                  img?.sourceUrl && (
                    <div
                      key={i}
                      className="relative w-full h-full overflow-hidden rounded-lg"
                    >
                      <Image
                        src={img.sourceUrl}
                        alt={img.altText || ""}
                        fill
                        className="object-cover"
                        sizes="100vw"
                      />
                    </div>
                  )
                );
              })}

            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoGallery;

PhotoGallery.fragments = {
  entry: gql`
    fragment PhotoGalleryFragment on FlexibleContentBlocksPhotoGallery {
      galleryTitle
      images(first: 6) {
        nodes {
          ...MediaItemFragment
        }
      }
    }
    ${MEDIA_ITEM_FRAGMENT}
  `,
};